/**
 * Email Validation Utilities
 * Provides comprehensive email validation for frontend forms
 */

// Common typo corrections for popular email domains
const DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hitmail.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'icloud.con': 'icloud.com',
  'icoud.com': 'icloud.com',
  'protonmal.com': 'protonmail.com',
  'protonmail.con': 'protonmail.com',
};

// Obviously fake/test domains to block
const BLOCKED_DOMAINS = [
  'test.com',
  'example.com',
  'example.org',
  'example.net',
  'fake.com',
  'asdf.com',
  'aaa.com',
  'abc.com',
  'temp-mail.org',
  'tempmail.com',
  'throwaway.email',
  'mailinator.com',
  'guerrillamail.com',
  'sharklasers.com',
  'yopmail.com',
  '10minutemail.com',
  'trashmail.com',
];

// Valid TLDs (most common - not exhaustive but catches typos)
const COMMON_TLDS = [
  'com', 'co.uk', 'org', 'net', 'edu', 'gov', 'io', 'co', 'me', 'info',
  'biz', 'us', 'uk', 'ca', 'au', 'de', 'fr', 'es', 'it', 'nl', 'be',
  'ch', 'at', 'se', 'no', 'dk', 'fi', 'ie', 'nz', 'in', 'jp', 'kr',
  'br', 'mx', 'ru', 'pl', 'cz', 'pt', 'gr', 'hu', 'ro', 'bg', 'hr',
  'sk', 'si', 'lt', 'lv', 'ee', 'is', 'za', 'sg', 'hk', 'tw', 'th',
  'my', 'ph', 'id', 'vn', 'ae', 'sa', 'il', 'tr', 'ua', 'by', 'kz',
  'app', 'dev', 'tech', 'online', 'site', 'website', 'email', 'cloud',
  'digital', 'agency', 'studio', 'design', 'media', 'group', 'team',
  'work', 'jobs', 'pro', 'expert', 'consulting', 'solutions', 'services',
];

/**
 * Validation result type
 */
export const ValidationStatus = {
  VALID: 'valid',
  INVALID_FORMAT: 'invalid_format',
  TYPO_DETECTED: 'typo_detected',
  BLOCKED_DOMAIN: 'blocked_domain',
  SUSPICIOUS_TLD: 'suspicious_tld',
  TOO_SHORT: 'too_short',
  MISSING_DOMAIN: 'missing_domain',
};

/**
 * Basic email format validation
 */
export const isValidEmailFormat = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // RFC 5322 compliant regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  return emailRegex.test(email.trim());
};

/**
 * Extract domain from email
 */
export const getDomain = (email) => {
  if (!email || !email.includes('@')) return null;
  return email.split('@')[1]?.toLowerCase();
};

/**
 * Check for common typos and suggest correction
 */
export const detectTypo = (email) => {
  const domain = getDomain(email);
  if (!domain) return null;
  
  if (DOMAIN_TYPOS[domain]) {
    const localPart = email.split('@')[0];
    return `${localPart}@${DOMAIN_TYPOS[domain]}`;
  }
  
  return null;
};

/**
 * Check if domain is blocked (disposable/fake)
 */
export const isBlockedDomain = (email) => {
  const domain = getDomain(email);
  if (!domain) return false;
  
  return BLOCKED_DOMAINS.some(blocked => 
    domain === blocked || domain.endsWith(`.${blocked}`)
  );
};

/**
 * Check if TLD looks suspicious
 */
export const hasSuspiciousTLD = (email) => {
  const domain = getDomain(email);
  if (!domain) return true;
  
  // Extract TLD (handle .co.uk style)
  const parts = domain.split('.');
  if (parts.length < 2) return true;
  
  const tld = parts.length > 2 && parts[parts.length - 2].length <= 3
    ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
    : parts[parts.length - 1];
  
  // Check against common TLDs
  // We don't block unknown TLDs, just flag them
  return !COMMON_TLDS.includes(tld.toLowerCase());
};

/**
 * Comprehensive email validation
 * Returns { status, message, suggestion }
 */
export const validateEmail = (email) => {
  const trimmed = email?.trim().toLowerCase();
  
  // Empty check
  if (!trimmed) {
    return {
      status: ValidationStatus.INVALID_FORMAT,
      message: 'Please enter your email address',
      suggestion: null,
      isValid: false,
    };
  }
  
  // Too short
  if (trimmed.length < 5) {
    return {
      status: ValidationStatus.TOO_SHORT,
      message: 'Email address is too short',
      suggestion: null,
      isValid: false,
    };
  }
  
  // Missing @
  if (!trimmed.includes('@')) {
    return {
      status: ValidationStatus.MISSING_DOMAIN,
      message: 'Please include an @ in the email address',
      suggestion: null,
      isValid: false,
    };
  }
  
  // Basic format check
  if (!isValidEmailFormat(trimmed)) {
    return {
      status: ValidationStatus.INVALID_FORMAT,
      message: 'Please enter a valid email address',
      suggestion: null,
      isValid: false,
    };
  }
  
  // Check for typos
  const suggestion = detectTypo(trimmed);
  if (suggestion) {
    return {
      status: ValidationStatus.TYPO_DETECTED,
      message: `Did you mean ${suggestion}?`,
      suggestion,
      isValid: true, // Allow submission but show warning
    };
  }
  
  // Check for blocked domains
  if (isBlockedDomain(trimmed)) {
    return {
      status: ValidationStatus.BLOCKED_DOMAIN,
      message: 'Please use a permanent email address, not a temporary one',
      suggestion: null,
      isValid: false,
    };
  }
  
  // Valid
  return {
    status: ValidationStatus.VALID,
    message: null,
    suggestion: null,
    isValid: true,
  };
};

/**
 * Real-time validation for input fields
 * Debounced version for use during typing
 */
export const getValidationState = (email, touched = false) => {
  if (!touched || !email) {
    return { showError: false, showWarning: false, message: null };
  }
  
  const result = validateEmail(email);
  
  if (result.status === ValidationStatus.TYPO_DETECTED) {
    return {
      showError: false,
      showWarning: true,
      message: result.message,
      suggestion: result.suggestion,
    };
  }
  
  if (!result.isValid) {
    return {
      showError: true,
      showWarning: false,
      message: result.message,
      suggestion: null,
    };
  }
  
  return { showError: false, showWarning: false, message: null };
};

export default validateEmail;
